import { useEffect, useState } from 'react'
import {
  getRegrasCadastroPacienteFci,
  RegrasCadastroPacienteFci,
  REGRAS_FCI_INATIVAS,
} from '../core/_fciRegras'

/**
 * Regras de obrigatoriedade da Ficha de Cadastro Individual para a empresa selecionada.
 *
 * Guardrail multi-tenant: a consulta é chaveada pelo `empresaId` do formulário — o mesmo id usado
 * em `createPaciente` —, nunca por um id global de sessão.
 *
 * DEGRADAÇÃO (AC8): qualquer falha (rede, 403, 5xx) devolve `REGRAS_FCI_INATIVAS`, ou seja, o
 * formulário abre com a obrigatoriedade de hoje e o submit continua permitido. O erro é registrado
 * no console. O gate do cliente é declaradamente **defesa em profundidade** sobre o 422 do backend
 * (`/usuarios/me` não expõe permissões efetivas), então falhar fechado aqui só impediria de
 * cadastrar quem tem direito.
 */
export const useRegrasFci = (empresaId?: number) => {
  const [regras, setRegras] = useState<RegrasCadastroPacienteFci>(REGRAS_FCI_INATIVAS)
  const [carregando, setCarregando] = useState(false)
  /**
   * Já houve uma resposta (ou uma falha) para esta empresa?
   *
   * Quem decide com base em `esusAtivo` **antes** disso decidiria com o padrão "inativo" e não teria
   * como voltar atrás — é o caso do preenchimento da raça legada "Não informado" (AC5).
   */
  const [resolvidas, setResolvidas] = useState(false)

  useEffect(() => {
    if (!empresaId) {
      setRegras(REGRAS_FCI_INATIVAS)
      setResolvidas(false)
      return
    }

    let cancelado = false
    setCarregando(true)
    setResolvidas(false)

    getRegrasCadastroPacienteFci(empresaId)
      .then((resposta) => {
        if (!cancelado) setRegras(resposta ?? REGRAS_FCI_INATIVAS)
      })
      .catch((error) => {
        if (cancelado) return
        console.error(
          '[e-SUS] Falha ao ler as regras de cadastro de paciente; o formulário segue com a ' +
            'obrigatoriedade padrão e a validação fica por conta do backend.',
          error
        )
        setRegras(REGRAS_FCI_INATIVAS)
      })
      .finally(() => {
        if (cancelado) return
        setCarregando(false)
        setResolvidas(true)
      })

    return () => {
      cancelado = true
    }
  }, [empresaId])

  return { regras, carregandoRegras: carregando, regrasResolvidas: resolvidas }
}
